import { UserApplicationDto } from '~/modules/Auth/Application/GetUser/UserApplicationDto'
import { FC, ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { UserContext } from '~/hooks/UserContext'
import { UserStatus } from '~/types/UserProviderInstance'
import { signOut, useSession } from 'next-auth/react'
import { UserProviderUserDtoTranslator } from '~/modules/Auth/Infrastructure/Translators/UserProviderUserDtoTranslator'
import { UserProviderUserDto } from '~/modules/Auth/Infrastructure/Dtos/UserProviderUserDto'

type FetchUserResult =
  | { type: 'success'; user: UserProviderUserDto }
  | { type: 'stale-session' }
  | { type: 'error' }

const UserProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const [status, setStatus] = useState<UserStatus>('SIGNED_OUT')
  const [user, setUser] = useState<UserProviderUserDto | null>(null)
  const session = useSession()

  const fetchUser = useCallback(async (): Promise<FetchUserResult> => {
    try {
      const response = await fetch('/api/auth')

      if (!response.ok) {
        if (response.status === 404) {
          const payload = await response.json().catch(() => null)

          if (payload?.code === 'users-user-not-found') {
            return { type: 'stale-session' }
          }
        }

        return { type: 'error' }
      }

      const authUser: UserApplicationDto = await response.json()

      return {
        type: 'success',
        user: UserProviderUserDtoTranslator.fromApplication(authUser),
      }
    } catch (exception: unknown) {
      console.error('UserProvider: Failed to fetch user data')
      console.error(exception)

      return { type: 'error' }
    }
  }, [])

  const getUser = useCallback(async (): Promise<void> => {
    const result = await fetchUser()

    if (result.type === 'success') {
      setStatus('SIGNED_IN')
      setUser(result.user)

      return
    }

    if (result.type === 'error') {
      return
    }

    setUser(null)
    setStatus('SIGNED_OUT')

    if (session.status === 'authenticated') {
      await signOut({ redirect: false })
    }
  }, [fetchUser, session.status])

  useEffect(() => {
    if (session.status === 'authenticated') {
      getUser()
        .catch((reason) => {
          console.log('UserProvider: getUser() could not update authenticated user data')
          console.log(reason)
        })

      return
    }

    if (session.status === 'unauthenticated') {
      setUser(null)
      setStatus('SIGNED_OUT')
    }
  }, [getUser, session.status])

  const updateUser = useCallback(async () => {
    await getUser()
  }, [getUser])

  const userInstance = useMemo(() => ({
    user,
    status,
    updateUser,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [
    session.status,
    status,
    user,
  ])

  return (
    <UserContext.Provider value={ { userInstance: userInstance } }>
      { children }
    </UserContext.Provider>
  )
}

export default UserProvider
